import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { makeStyles, useMediaQuery } from '@material-ui/core';
import cx from 'classnames';
import useDetectScrollEnd from '../../../../hooks/useDetectScrollEnd';
import { getScrollbarStyles } from '../../../../utils/getStyles/getStyles';
import styles from './Terms.module.scss';
import sanitizer from '../../../../services/sanitize-service';
import { primaryColorSelector } from '../../../../store/selectors/globalConfigSelectors';
import { termsCurrentStepSelector } from '../../../../store/selectors/termsConfigSelectors';

const SCROLL_END_TOLERANCE_PX = 10;

const useTermStyles = (isMobileView) =>
  makeStyles((theme) => ({
    wrapper: {
      overflowY: isMobileView ? 'visible' : 'scroll',
      padding: isMobileView ? 0 : 20,
      ...(!isMobileView ? { maxHeight: '300px' } : {}),
      ...getScrollbarStyles(theme),
    },
  }));

const Terms = ({ setTermsRead, termContent }) => {
  const termsRef = useRef(null);
  const { primaryColor, currentStep } = useSelector((state) => ({
    primaryColor: primaryColorSelector(state),
    currentStep: termsCurrentStepSelector(state),
  }));

  const isMobileView = useMediaQuery((theme) => theme.breakpoints.down('xs'));
  const classes = useTermStyles(isMobileView)();

  useDetectScrollEnd(termsRef, setTermsRead);

  // hack for dynamic change style scrollbar
  document.documentElement.style.setProperty('--main-color', primaryColor);

  useEffect(() => {
    const { current } = termsRef;
    if (!current) return undefined;

    current.scrollTo(0, 0);

    // Same intent as before (auto-enable when no scroll is needed), but:
    // 1) use the same +10px tolerance as scroll-end detection (not strict ===)
    // 2) re-check after layout so short content is measured correctly
    // 3) also re-run when termContent changes (multi-step / updated HTML)
    const checkIfScrollNotNeeded = () => {
      if (current.scrollHeight - current.scrollTop < current.clientHeight + SCROLL_END_TOLERANCE_PX) {
        setTermsRead(true);
      }
    };

    const frameId = requestAnimationFrame(checkIfScrollNotNeeded);
    return () => cancelAnimationFrame(frameId);
  }, [currentStep, termContent, setTermsRead]);

  return (
    <div
      ref={termsRef}
      className={cx(classes.wrapper, styles.wrapper)}
      dangerouslySetInnerHTML={{ __html: sanitizer(termContent) }}
    />
  );
};

Terms.propTypes = {
  setTermsRead: PropTypes.func.isRequired,
  termContent: PropTypes.string.isRequired,
};

export default Terms;
